// Create/edit form modal (Step 5e Phase 7a) — renders a field grid, builds the API payload,
// and surfaces server errors + warnings.

import { useState } from 'react';
import { C, Modal, Btn, ErrorText, WarnText, FieldGrid, buildPayload, type FormValues } from './adminUi';
import type { AdminFieldDef } from './fields';
import type { Result } from '../../lib/admin';

export default function FormModal({ title, fields, initial, readOnlyHeader, onSubmit, onClose, onSaved }: {
  title: string;
  fields: AdminFieldDef[];
  initial?: FormValues;
  readOnlyHeader?: string;
  onSubmit: (payload: Record<string, unknown>) => Promise<Result<unknown>>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [values, setValuesRaw] = useState<FormValues>(initial ?? {});
  const [touched, setTouched] = useState(false);
  // Mark the form dirty on any user edit so a stray backdrop click / Esc prompts before discarding.
  const setValues = (v: FormValues) => { setTouched(true); setValuesRaw(v); };
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // The record IS saved; the modal is held open only so an advisory is read rather than
  // flashed. ⚠ Previously setWarnings() was followed immediately by onSaved(), which unmounts
  // this component — so every warning the server returned (the ICAO registry advisory, and now
  // the session-hours one) was discarded on the same tick and never reached anybody.
  const [savedWithWarnings, setSavedWithWarnings] = useState(false);

  async function submit() {
    setSaving(true); setError(null); setWarnings([]);
    const payload = buildPayload(fields, values);
    const res = await onSubmit(payload);
    setSaving(false);
    if (res.error) { setError(res.error); return; }
    if (res.warnings.length) { setWarnings(res.warnings); setSavedWithWarnings(true); setTouched(false); return; }
    onSaved();
  }

  return (
    <Modal title={title} onClose={onClose} width={680} dirty={touched}>
      {readOnlyHeader && <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10 }}>{readOnlyHeader}</div>}
      <FieldGrid fields={fields} values={values} setValues={setValues} />
      {error && <ErrorText>{error}</ErrorText>}
      {savedWithWarnings && <div style={{ fontSize: 13, color: C.text, marginTop: 10 }}>Saved. Please note:</div>}
      {warnings.map((w, i) => <WarnText key={i}>{w}</WarnText>)}
      <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        {savedWithWarnings
          ? <Btn kind="primary" onClick={onSaved}>Done</Btn>
          : <>
              <Btn kind="primary" disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save'}</Btn>
              <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
            </>}
      </div>
    </Modal>
  );
}
